"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { createClient } from "@/lib/supabase";
import type { Project } from "@/types/database";
import { AnimateSection, StaggerContainer, StaggerItem } from "@/components/AnimateSection";
import { TiltCard } from "@/components/TiltCard";

const FILTERS = ["All", "Frontend", "Backend", "DevOps", "Fullstack"];

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from("projects")
      .select("*")
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        setProjects(data ?? []);
        setLoading(false);
      });
  }, []);

  const filtered =
    activeFilter === "All"
      ? projects
      : projects.filter((p) =>
        (p.tech_stack ?? []).some((t) =>
          t.toLowerCase().includes(activeFilter.toLowerCase())
        )
      );

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
        <h1 className="mb-8 text-4xl font-bold text-dev-text-primary sm:text-5xl">
          Projects
        </h1>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="clay-card h-72 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
      <AnimateSection>
        <h1 className="text-4xl font-bold text-dev-text-primary sm:text-5xl">
          My <span className="text-gradient">Projects</span>
        </h1>
        <p className="mt-4 text-dev-text-muted">
          A collection of projects I&apos;ve built and contributed to
        </p>
      </AnimateSection>

      {/* Filter Tabs */}
      <AnimateSection className="mt-10" delay={0.1}>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`relative rounded-xl px-5 py-2 text-sm font-medium transition-all duration-300 ${activeFilter === filter
                  ? "text-dev-cyan"
                  : "text-dev-text-muted hover:text-dev-text-secondary"
                }`}
            >
              {activeFilter === filter && (
                <motion.span
                  layoutId="filter-active"
                  className="absolute inset-0 rounded-xl bg-dev-blue/10 border border-dev-blue/20"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10">{filter}</span>
            </button>
          ))}
        </div>
      </AnimateSection>

      {/* Projects Grid */}
      <div className="mt-8">
        {filtered.length === 0 ? (
          <p className="text-dev-text-secondary py-12 text-center">
            No projects found for this filter.
          </p>
        ) : (
          <motion.div
            layout
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <TiltCard className="group h-full">
                    <Link
                      href={`/projects/${encodeURIComponent(project.slug)}/`}
                      className="clay-card block h-full overflow-hidden cursor-pointer"
                    >
                      {project.image_url && (
                        <div className="relative aspect-video overflow-hidden rounded-t-[20px] bg-dev-bg-medium">
                          <Image
                            src={project.image_url}
                            alt={project.title}
                            width={800}
                            height={450}
                            unoptimized
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-dev-bg-dark/90 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center justify-between">
                          <h2 className="font-semibold text-dev-text-primary transition-colors group-hover:text-dev-cyan">
                            {project.title}
                          </h2>
                          {project.featured && (
                            <span className="status-badge active">Featured</span>
                          )}
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-dev-text-secondary">
                          {project.description}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {(project.tech_stack ?? []).slice(0, 4).map((tech) => (
                            <span key={tech} className="tech-tag">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  </TiltCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
}
